<template>
    <div class="p-4">
        <div class="mb-4 mt-2 d-flex align-items-center justify-content-center flex-column" style="font-size: 110%">
            <div>
                <p class="text-secondary">Select a learning experience to add to your course</p>
            </div>
            <div>
                <ul class="nav">
                    <li class="nav-item tab-nav tab-nav-highlight mr-2" role="button" @click="setCurrentTab('owned')">
                        <div class="nav-link tab-nav-link" :class="{ active: currentTab === 'owned' }">My Projects</div>
                    </li>
                    <li class="nav-item tab-nav tab-nav-highlight mr-2" role="button" @click="setCurrentTab('library')">
                        <div class="nav-link tab-nav-link" :class="{ active: currentTab === 'library' }">Library</div>
                    </li>
                    <li
                        v-if="showCommunityTab"
                        class="nav-item tab-nav tab-nav-highlight mr-1"
                        role="button"
                        @click="setCurrentTab('community')"
                    >
                        <div class="nav-link tab-nav-link" :class="{ active: currentTab === 'community' }">
                            Community
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        <div class="mb-4 pb-2 d-flex justify-content-center">
            <div class="d-flex justify-content-center align-items-center position-relative">
                <input
                    type="text"
                    class="form-control"
                    :placeholder="searchPlaceholder"
                    style="width: 310px"
                    v-model="searchQuery"
                    @input="search"
                />
                <div
                    v-show="searchIsLoading"
                    class="spinner-container justify-content-center align-items-center"
                    :class="{ 'd-flex': searchIsLoading }"
                >
                    <span class="mx-1">
                        <span
                            class="spinner-border spinner-border-sm text-primary"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        <span class="sr-only">Loading...</span>
                    </span>
                </div>
            </div>
        </div>
        <!-- Org Library Items -->
        <div class="d-flex justify-content-center" v-if="libraryTabActive">
            <ul class="grid flex-grow-1 justify-content-center">
                <li
                    class="d-flex flex-column item pointer"
                    v-for="item in libraryItems"
                    :key="'library-' + item.id"
                    @click="selectItem(item, null)"
                >
                    <div class="d-flex flex-column entry-card flex-grow-1 position-relative">
                        <!-- Card Cover Image -->
                        <div class="flex-grow-0">
                            <img
                                v-if="item.case.cover_image"
                                :src="item.case.cover_image.signed_url"
                                class="image-cover flex-grow-0"
                                :alt="item.case.cover_image_alt_text"
                                loading="lazy"
                            />
                            <div v-else class="image-cover d-flex align-items-center justify-content-center">
                                <span class="h5 mb-0 px-2 font-weight-bold text-center cover-placeholder">{{
                                    item.case.public_title
                                }}</span>
                            </div>
                            <div
                                v-if="item.item_type === 'community_reference'"
                                class="position-absolute text-white community-watermark"
                            >
                                <span class="icon icon-glyphicons-basic-world-east"></span>
                            </div>
                        </div>
                        <!-- Card Body -->
                        <div class="d-flex flex-column flex-grow-1 entry-card-body">
                            <!-- Description & Metadata -->
                            <div class="entry-card-metadata flex-grow-1 d-flex flex-column community-text-body py-2">
                                <div class="flex-grow-0">
                                    <div class="d-flex mb-2 scenario-title">
                                        {{ item.title }}
                                    </div>
                                    <div>
                                        <p class="small text-secondary scenario-description mb-0">
                                            {{ item.description }}
                                        </p>
                                    </div>
                                    <div class="mt-2" v-if="item.item_type === 'community_reference'">
                                        <a
                                            @click.stop
                                            class="badge badge-gray case-badge mr-1"
                                            :href="viewInCommunityLink(item)"
                                            target="_blank"
                                        >
                                            From Community
                                            <span class="icon icon-glyphicons-basic-square-new-window"></span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
        <!-- Community Items -->
        <div v-else>
            <div class="d-flex justify-content-center">
                <ul class="grid flex-grow-1 justify-content-center">
                    <li class="d-flex flex-column item" v-for="caseObj in communityItems" :key="caseObj.id">
                        <community-case-card
                            :case-obj="caseObj"
                            :show-add-actions="false"
                            :open-nav-links-in-tabs="true"
                            :cover-height-px="170"
                            :show-preview-link="true"
                            v-on:item-selected="selectItem(null, $event)"
                        ></community-case-card>
                    </li>
                </ul>
            </div>
        </div>
        <div v-show="loadingMore">
            <div class="d-flex justify-content-center align-items-end scroll-loading-container">
                <div class="d-flex justify-content-center align-items-center mb-2">
                    <div class="custom-spinner" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        </div>
        <div v-show="!loading && !loadingMore && !allLoaded && !searchIsLoading">
            <div class="d-flex justify-content-center align-items-end scroll-loading-container">
                <button type="button" class="btn btn-gray" @click="loadMore()">Load More</button>
            </div>
        </div>
        <div v-show="!loading && currentListIsEmpty && !loadingMore && !searchIsLoading">
            <div class="d-flex justify-content-center py-5">
                <div class="font-weight-bold text-secondary">
                    <span v-if="searchQuery">No items found matching your search.</span>
                    <span v-else>
                        <span v-if="currentTab === 'community'">No items found.</span>
                        <span v-else-if="currentTab === 'owned'"
                            >You haven't been added to any projects in Affinity yet.</span
                        >
                        <span v-else>No items have been made visible in your LMS yet.</span>
                    </span>
                </div>
            </div>
        </div>
        <form id="deepLinkRedirectForm" :action="ltiDeepLinkReturnUrl" method="POST" class="d-none">
            <input type="hidden" name="JWT" :value="ltiDeepLinkResponseJWT" />
        </form>
        <v-style> html, body, #app { background-color: #fff !important; } </v-style>
        <base-modal ref="linkSettingsModal" :centered="true">
            <template>
                <base-form
                    class="mt-1"
                    v-model="linkSettings"
                    :fields="linkSettingsFormFields"
                    :condensedStyle="true"
                ></base-form>
            </template>
            <template slot="footer">
                <button :disabled="loading" class="btn btn-gray" data-dismiss="modal">Cancel</button>
                <button :disabled="loading" @click="completeRequest()" class="btn btn-primary" data-dismiss="modal">
                    Add to Course
                </button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import Utilities from "@/assets/js/utilities.js"
import http from "@/axios.js"
import debounce from "lodash.debounce"
import CommunityCaseCard from "@/components/CommunityCaseCard"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"

export default {
    name: "LmsContentSelector",
    data() {
        return {
            currentTab: "owned",
            launchRequest: {
                lms_instance: {},
            },
            libraryItems: [],
            communityItems: [],
            searchQuery: "",
            searchIsLoading: false,
            searchId: null,
            selectedLibraryItem: null,
            ltiDeepLinkReturnUrl: null,
            ltiDeepLinkResponseJWT: null,
            pageSize: 10,
            startIndex: 0,
            loadingMore: false,
            allLoaded: false,
            initialLoadComplete: false,
            infiniteScrollListener: null,
            payload: null,
            selectedItemTitle: "",
            linkSettings: {
                enableGrading: true,
            },
        }
    },
    components: {
        CommunityCaseCard,
        BaseModal,
        BaseForm,
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        launchRequestId() {
            return this.$route.params.id
        },
        libraryTabActive() {
            return ["library", "owned"].includes(this.currentTab)
        },
        searchPlaceholder() {
            if (this.currentTab === "owned") {
                return "Search your content..."
            } else if (this.currentTab === "library") {
                return "Search your organization's library..."
            } else {
                return "Search the community library..."
            }
        },
        redirectFormIsValid() {
            return this.ltiDeepLinkReturnUrl && this.ltiDeepLinkResponseJWT
        },
        currentListIsEmpty() {
            let libraryItemsViewEmpty = this.libraryItems.length === 0 && this.libraryTabActive
            let communityItemsViewEmpty = this.communityItems.length === 0 && this.currentTab === "community"
            return libraryItemsViewEmpty || communityItemsViewEmpty
        },
        showCommunityTab() {
            return this.launchRequest.lms_instance.show_community_items_in_dp_link
        },
        linkSettingsFormFields() {
            let vm = this
            return [
                {
                    name: "Learners will receive a grade for this activity",
                    key: "enableGrading",
                    type: "switch",
                    subtext:
                        `Check this box if you want learners to receive a grade for this activity.
                        Grades will automatically be synced with your LMS.`,
                },
                {
                    name: "Require manual grading by an instructor",
                    key: "requireManualGrading",
                    type: "switch",
                    subtext:
                        "Check this box if you want an instructor to review grades before they're released to students.",
                    disabled() {
                        return !vm.linkSettings.enableGrading
                    },
                },
            ]
        },
    },
    methods: {
        // Load more items.
        loadMore() {
            this.startIndex += this.pageSize
            this.loadingMore = true
            this.searchDebounced()
        },
        // Trigger a debounced search of library items.
        search() {
            // Always reset pagination when the search query changes.
            this.startIndex = 0
            this.searchIsLoading = true
            this.searchDebounced(true)
        },
        // Debounced fetch of library items.
        searchDebounced: debounce(async function (replace = false) {
            // Store a random search to make sure that an older search query doesn't
            // overwrite new search results.
            let searchId = Utilities.getRandomIdStr(10)
            this.searchId = searchId
            if (this.libraryTabActive) {
                this.searchLibrary(searchId, replace)
            } else {
                this.searchCommunity(searchId, replace)
            }
        }, 150),
        // Search the organization's library.
        searchLibrary(searchId, replace = false) {
            let vm = this
            let queryType = this.currentTab === "owned" ? "own_library" : "org_library"
            http.get(`/api/lms/launch/${this.launchRequestId}/deep-linking/library-items/`, {
                params: {
                    query: this.searchQuery,
                    queryType: queryType,
                    startIndex: this.startIndex,
                    endIndex: this.startIndex + this.pageSize,
                },
            })
                .then((response) => {
                    if (searchId === vm.searchId) {
                        vm.searchIsLoading = false
                        vm.loadingMore = false
                    } else {
                        // New search was dispatched since, don't take any action.
                        return
                    }
                    if (replace) {
                        vm.libraryItems = response.data
                    } else {
                        vm.libraryItems = vm.libraryItems.concat(response.data)
                    }
                    if (response.data.length < this.pageSize) {
                        vm.allLoaded = true
                    }
                    if (response.data.length !== 0) {
                        vm.initialLoadComplete = true
                    }
                    if (response.data.length === 0 && !vm.initialLoadComplete) {
                        // No owned items, so switch to a different tab initially.
                        if (vm.currentTab === "owned") {
                            // Switch to library tab.
                            vm.setCurrentTab("library")
                        } else if (vm.currentTab === "library" && vm.showCommunityTab) {
                            // Switch to community tab.
                            vm.setCurrentTab("community")
                        }
                    }
                })
                .catch(() => {
                    vm.searchIsLoading = false
                    vm.loadingMore = false
                    vm.initialLoadComplete = true
                })
        },
        // Search the community library.
        searchCommunity(searchId, replace = false) {
            let vm = this
            http.get("/api/community/cases/browse/", {
                params: {
                    query: this.searchQuery,
                    startIndex: this.startIndex,
                    endIndex: this.startIndex + this.pageSize,
                },
            })
                .then((response) => {
                    if (searchId !== vm.searchId) {
                        // New search was dispatched since, don't take any action.
                        return
                    }
                    if (replace) {
                        vm.communityItems = response.data
                    } else {
                        vm.communityItems = vm.communityItems.concat(response.data)
                    }
                    if (response.data.length < this.pageSize) {
                        vm.allLoaded = true
                    }
                })
                .finally(() => {
                    if (searchId === vm.searchId) {
                        vm.searchIsLoading = false
                        vm.loadingMore = false
                    }
                })
        },
        // Set the current tab being viewed.
        setCurrentTab(tab) {
            if (tab !== this.currentTab) {
                this.libraryItems = []
                this.communityItems = []
                this.startIndex = 0
                this.currentTab = tab
                this.allLoaded = false
                this.loadingMore = true
                this.searchQuery = ""
                this.searchDebounced()
            }
        },
        // Format a date object as a string.
        formatDate(dateObj) {
            return Utilities.formatDate(dateObj)
        },
        // Select a library item or community case to send back to the LMS (to complete the
        // deep linking request).
        selectItem(item = null, caseObj = null) {
            if (item) {
                this.payload = {
                    libraryItemId: item.id,
                }
                this.selectedItemTitle = item.case.public_title
            } else {
                this.payload = {
                    caseId: caseObj.id,
                }
                this.selectedItemTitle = caseObj.community_tile
            }
            this.$refs.linkSettingsModal.show()
        },
        completeRequest() {
            this.$store.dispatch("addLoad")
            this.$refs.linkSettingsModal.hide()
            let vm = this
            http.post(`/api/lms/launch/${this.launchRequestId}/deep-linking/`, {
                ...this.payload,
                enableGrading: this.linkSettings.enableGrading,
                requireManualGrading: this.linkSettings.requireManualGrading,
            })
                .then((response) => {
                    vm.ltiDeepLinkReturnUrl = response.data.return_url
                    vm.ltiDeepLinkResponseJWT = response.data.jwt
                    // Only send the deep linking response if running in production.
                    if (!document.location.hostname.startsWith("localhost")) {
                        vm.$nextTick(() => {
                            document.getElementById("deepLinkRedirectForm").submit()
                        })
                    }
                })
                .catch(() => {
                    // Only stop the load spinner if there's an error, because the
                    // LMS will redirect from this view automatically. If we stop the
                    // loading spinner on success, there's a delay while waiting for the LMS
                    // to redirect away from the view.
                    vm.$store.dispatch("removeLoad")
                })
        },
        // Returns a link to the community view for a published case.
        viewInCommunityLink(item) {
            return this.$router.resolve({ name: "CommunityCaseDetail", params: { id: item.case.id } }).href
        },
    },
    async mounted() {
        this.$store.dispatch("addLoad")
        // Initialize infinite scrolling behaviour.
        let vm = this
        let rootApp = document.getElementById("app")
        // Add scroll event listener to load more items when the user scrolls to the bottom of the page.
        this.infiniteScrollListener = debounce(() => {
            let atBottom = document.documentElement.scrollTop + window.innerHeight >= rootApp.offsetHeight - 800
            if (atBottom && !vm.loadingMore && !vm.allLoaded) {
                vm.loadMore()
            }
        }, 30)
        window.addEventListener("scroll", this.infiniteScrollListener)
        http.get(`/api/lms/launch/${this.launchRequestId}/deep-linking/`)
            .then((response) => {
                vm.launchRequest = response.data
                // Fetch initial data.
                vm.loadingMore = true
                vm.searchDebounced()
            })
            .finally(() => {
                vm.$store.dispatch("removeLoad")
            })
    },
    beforeDestroy() {
        // Kill infinite scroll event listener.
        window.removeEventListener("scroll", this.infiniteScrollListener)
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/community.scss";

.scenario-title {
    font-weight: 500;
    color: $body-color !important;
}

.scenario-description {
    margin-bottom: 8px;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
}

.image-cover {
    height: 170px;
    width: 100%;
    object-fit: cover;
    border-top-left-radius: $border-radius-lg;
    border-top-right-radius: $border-radius-lg;
}

.entry-card {
    background-color: #ffffff;
    border-radius: $border-radius-lg;
    box-shadow: $box-shadow-sm;

    &:hover {
        .entry-card-more-btn {
            color: inherit;
        }
    }
}

.entry-card-body {
    padding: 16px;
}

.entry-card-metadata {
    color: $body-color !important;
}

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
    grid-template-rows: 1fr;
    padding-inline-start: 0px;
    grid-gap: 30px;
    @media (min-width: $breakpoint-xl) {
        grid-gap: 36px;
    }
    @media (min-width: 1550px) {
        grid-gap: 38px;
    }
}

.spinner-container {
    position: absolute;
    right: 10px;
}

.spinner-border-sm {
    border-width: 0.15em;
}

.scroll-loading-container {
    height: 120px;
}

.community-watermark {
    right: 6px;
    top: 132px;
    font-size: 24px;
}
</style>